export const EmailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export type SubscriptionStatus = 'free' | 'canceled' | 'active';

export interface AiQuota {
  total?: number | null;
  remaining: number | null; // null = unlimited, 0 = exhausted
  show_remaining: boolean;
  period?: 'daily' | 'monthly';
  reset_at?: string;
}

export interface PlanQuota {
  document: AiQuota;
  deep_research: AiQuota;
  ai_ask_ai: AiQuota;
  ai_summarize: AiQuota;
  ai_expand: AiQuota;
  ai_find_references: AiQuota;
  ai_generate_image: AiQuota;
}

export type AiFeatureType =
  | 'ask_ai'
  | 'summarize'
  | 'expand'
  | 'find_references'
  | 'generate_image';

export interface AiFeatureStatus {
  isLocked: boolean;
  remaining: number | null;
  total: number | null;
  showRemaining: boolean;
  period?: 'daily' | 'monthly';
  resetAt?: string;
}

export interface UserPlan {
  name: string;
  type: string;
  end_date: string | null;
  cancel_at_period_end?: boolean;
  purchase_source?: string | null;
  quota?: PlanQuota;
}

export interface UserInfo {
  email: string;
  is_internal?: boolean;
  customerPortal?: string;
  terms_and_privacy_accepted?: boolean;
  in_progress_documents?: number;
  total_documents?: number;
  plan?: UserPlan;
}

export interface PlanStatus {
  isFree: boolean;
  isAdvanced: boolean;
  isPro: boolean;
  isCanceled: boolean;
  isReachedLimit: boolean;
  isLimited: boolean;
  isPlanReachedLimit: boolean;
  isMarketplaceUser: boolean;
  cannotUpgrade: boolean;
  subscriptionStatus: string;
  remainingDocuments?: number | null;
  totalDocuments?: number | null;
  inProgressDocuments?: number;
  endDate?: string | null;
  limitType?: string;
  limitInfos?: Record<string, any>;
  showRemainingDocuments: boolean;
  documentPeriod?: 'daily' | 'monthly';
  documentResetAt?: string;
  remainingDeepResearch: number | null;
  totalDeepResearch?: number | null;
  showRemainingDeepResearch: boolean;
  deepResearchPeriod?: 'daily' | 'monthly';
  deepResearchResetAt?: string;
  isDeepResearchLocked: boolean;
  getAiFeatureStatus: (type: AiFeatureType) => AiFeatureStatus;
}

export type SubscriptionStatusInfo = {
  text: string;
  color: string;
  bgColor: string;
};

const subscriptionStatusInfo: Record<string, SubscriptionStatusInfo> = {
  active: { text: 'Renews', color: '#005231', bgColor: '#DAF5EA' },
  free: { text: 'Ends', color: '#8A4908', bgColor: '#FFEBC2' },
  canceled: { text: 'Expires', color: '#8A4908', bgColor: '#FFEBC2' }
};

const userPlanInfo: Record<
  string,
  { name?: string; remainingDocumentsText?: string }
> = {
  free: { name: 'Free Trial' },
  professional: { remainingDocumentsText: 'Unlimited documents' }
};

export { subscriptionStatusInfo, userPlanInfo };
export type GAUserType =
  | 'unknown'
  | 'internal'
  | 'external_trial'
  | 'external_paid';

export type GAUserPlan =
  | 'advanced'
  | 'professional'
  | 'free'
  | 'expired'
  | 'null';

export type PublicationDateValue = 'all' | 'last_5_years' | 'last_10_years';

export type CitationFilterSettings = {
  publication_date: PublicationDateValue;
  article_types: string[];
  top_cited?: boolean;
};

export type CitationSettings = {
  pmc: CitationFilterSettings;
  pubmed: CitationFilterSettings & { same_as_pmc: boolean };
};
